import { useCallback, useEffect, useMemo, useState } from 'react';
import clienteAxios from '../../config/axios';
import { useT } from 'i18n';
import TarjetaProyecto from './TarjetaProyecto';
import PanelOperacion from './PanelOperacion';
import { avance, diasHasta, money, nombrePais, normaliza, num, textoDias } from './utils';
import './mercado.css';

const VACIO = { q: '', pais: '', categoria: '', riesgo: '', vence: '', orden: 'destacados' };

const VENCE = [
  { id: '', tk: 'mercado.plazoCualquiera' },
  { id: '30', tk: 'mercado.plazo30' },
  { id: '90', tk: 'mercado.plazo90' },
  { id: '365', tk: 'mercado.plazo365' },
];

const ORDEN = [
  { id: 'destacados', tk: 'mercado.ordDestacados' },
  { id: 'avance', tk: 'mercado.ordAvance' },
  { id: 'tasa', tk: 'mercado.ordTasa' },
  { id: 'vence', tk: 'mercado.ordVence' },
  { id: 'monto', tk: 'mercado.ordMonto' },
];

const unicos = (lista, campo) => [...new Set(lista.map((p) => p[campo]).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b), 'es'));

const Mercado = () => {
  const { t } = useT();
  const [mercado, setMercado] = useState('primario');
  const [vista, setVista] = useState('tarjetas');
  const [datos, setDatos] = useState({ primario: [], secundario: [] });
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [f, setF] = useState(VACIO);
  const [abierto, setAbierto] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      const [a, b] = await Promise.all([
        clienteAxios.post('/api/client/projects/all_projects', {}),
        clienteAxios.post('/api/client/projects/secundario', { todo: 1 }),
      ]);
      setDatos({ primario: a.data.data || [], secundario: b.data.data || [] });
    } catch (e) {
      setError(true);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const base = datos[mercado];
  const opciones = useMemo(() => ({
    paises: unicos(base, 'country_iso'),
    categorias: unicos(base, 'category'),
    riesgos: unicos(base, 'risk'),
  }), [base]);

  const lista = useMemo(() => {
    const q = normaliza(f.q.trim());
    let r = base.filter((p) => {
      if (q && !normaliza([p.name, p.nrocontrato, p.code, p.category, nombrePais(p.country_iso), p.issuer, p.oferente?.name, p.description].join(' ')).includes(q)) return false;
      if (f.pais && p.country_iso !== f.pais) return false;
      if (f.categoria && p.category !== f.categoria) return false;
      if (f.riesgo && p.risk !== f.riesgo) return false;
      if (f.vence) {
        const d = diasHasta(p.end_date);
        if (d === null || d > Number(f.vence)) return false;
      }
      return true;
    });
    const clave = {
      avance: (p) => -avance(p),
      tasa: (p) => -num(p.tax_referential_client),
      vence: (p) => diasHasta(p.end_date) ?? 99999,
      monto: (p) => num(p.amount),
    }[f.orden];
    if (clave) r = [...r].sort((a, b) => clave(a) - clave(b));
    return r;
  }, [base, f]);

  const activos = ['q', 'pais', 'categoria', 'riesgo', 'vence'].filter((k) => f[k]);
  const set = (k) => (e) => { const v = e.target.value; setF((x) => ({ ...x, [k]: v })); };
  const secundario = mercado === 'secundario';

  return (
    <div className="content p7m">
      <header className="p7m-head">
        <div>
          <h2>{t('mercado.titulo')}</h2>
          <p>{t('mercado.sub')}</p>
        </div>
        <div className="p7-seg" role="tablist" aria-label={t('mercado.ariaMercado')}>
          <button type="button" role="tab" aria-selected={!secundario} className={!secundario ? 'on' : ''} onClick={() => { setMercado('primario'); setF(VACIO); }}>
            {t('mercado.primario')} <span className="p7m-n">{datos.primario.length}</span>
          </button>
          <button type="button" role="tab" aria-selected={secundario} className={secundario ? 'on' : ''} onClick={() => { setMercado('secundario'); setF(VACIO); }}>
            {t('mercado.secundario')} <span className="p7m-n">{datos.secundario.length}</span>
          </button>
        </div>
      </header>

      <p className="p7m-explica">
        {secundario
          ? t('mercado.explicaSec')
          : t('mercado.explicaPri')}
      </p>

      <section className="p7m-filtros" aria-label={t('mercado.filtros')}>
        <input type="search" className="p7m-buscar" placeholder={t('mercado.buscarPh')} value={f.q} onChange={set('q')} aria-label={t('mercado.buscarAria')} />
        <select value={f.categoria} onChange={set('categoria')} aria-label={t('mercado.categoria')}>
          <option value="">{t('mercado.todasCat')}</option>
          {opciones.categorias.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={f.pais} onChange={set('pais')} aria-label={t('mercado.pais')}>
          <option value="">{t('mercado.todosPaises')}</option>
          {opciones.paises.map((c) => <option key={c} value={c}>{nombrePais(c)}</option>)}
        </select>
        <select value={f.riesgo} onChange={set('riesgo')} aria-label={t('mercado.riesgo')}>
          <option value="">{t('mercado.todoRiesgo')}</option>
          {opciones.riesgos.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={f.vence} onChange={set('vence')} aria-label={t('mercado.plazo')}>
          {VENCE.map((v) => <option key={v.id} value={v.id}>{t(v.tk)}</option>)}
        </select>
        <select value={f.orden} onChange={set('orden')} aria-label={t('mercado.ordenar')}>
          {ORDEN.map((o) => <option key={o.id} value={o.id}>{t(o.tk)}</option>)}
        </select>
      </section>

      <div className="p7m-barra-estado">
        <span>{cargando ? t('mercado.cargando') : t(lista.length === 1 ? 'mercado.nProyecto' : 'mercado.nProyectos', { n: lista.length })}</span>
        {activos.length > 0 && <button type="button" className="p7m-limpiar" onClick={() => setF((x) => ({ ...VACIO, orden: x.orden }))}>{t(activos.length === 1 ? 'mercado.quitarFiltro' : 'mercado.quitarFiltros', { n: activos.length })}</button>}
        <div className="p7-seg p7m-vista" role="group" aria-label={t('mercado.vista')}>
          <button type="button" className={vista === 'tarjetas' ? 'on' : ''} onClick={() => setVista('tarjetas')}>{t('mercado.tarjetas')}</button>
          <button type="button" className={vista === 'tabla' ? 'on' : ''} onClick={() => setVista('tabla')}>{t('mercado.tabla')}</button>
        </div>
      </div>

      {error && <div className="p7m-vacio"><p>{t('mercado.errorCarga')}</p><button type="button" className="p7-btn-gold" onClick={cargar}>{t('mercado.reintentar')}</button></div>}

      {cargando && !error && (
        <div className="p7m-grid" aria-busy="true">
          {[0, 1, 2].map((i) => <div key={i} className="p7m-card p7m-esqueleto" />)}
        </div>
      )}

      {!cargando && !error && lista.length === 0 && (
        <div className="p7m-vacio">
          <p>{base.length === 0 ? (secundario ? t('mercado.sinSecundario') : t('mercado.sinPrimario')) : t('mercado.sinCoincidencia')}</p>
          {base.length > 0 && <button type="button" className="p7-btn-ghost" onClick={() => setF(VACIO)}>{t('mercado.quitarFiltrosBtn')}</button>}
        </div>
      )}

      {!cargando && !error && lista.length > 0 && vista === 'tarjetas' && (
        <div className="p7m-grid">
          {lista.map((p) => <TarjetaProyecto key={`${p.id}-${p.offer_id || ''}`} p={p} secundario={secundario} onAbrir={setAbierto} />)}
        </div>
      )}

      {!cargando && !error && lista.length > 0 && vista === 'tabla' && (
        <div className="p7-table-wrap">
          <table className="p7-table">
            <thead>
              <tr><th>{t('mercado.proyecto')}</th><th>{t('mercado.categoria')}</th><th>{t('mercado.pais')}</th><th className="p7-num">{t('mercado.tasa')}</th><th>{t('mercado.venceEn')}</th><th>{t('mercado.riesgo')}</th><th className="p7-num">{secundario ? t('mercado.precio') : t('mercado.avance')}</th><th /></tr>
            </thead>
            <tbody>
              {lista.map((p) => (
                <tr key={`${p.id}-${p.offer_id || ''}`}>
                  <td><strong>{p.name}</strong><span className="p7-sub">{p.nrocontrato}</span></td>
                  <td>{p.category}</td>
                  <td>{nombrePais(p.country_iso)}</td>
                  <td className="p7-num">{money(p.tax_referential_client)}%</td>
                  <td>{textoDias(diasHasta(p.end_date))}</td>
                  <td>{p.risk}</td>
                  <td className="p7-num">{secundario ? `${money(p.precioventa)} USDT` : `${avance(p)}%`}</td>
                  <td><button type="button" className="p7-btn-gold p7m-mini" onClick={() => setAbierto(p)}>{secundario ? t('mercado.comprar') : t('mercado.invertir')}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {abierto && <PanelOperacion p={abierto} secundario={secundario} onCerrar={() => setAbierto(null)} onHecho={cargar} />}
    </div>
  );
};

export default Mercado;
